import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine, ReferenceDot, ResponsiveContainer, Legend, Customized
} from 'recharts';
import { TrendingUp, TrendingDown, Minus, Loader2, RefreshCw } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { useLiveDashboard } from '../hooks/useLiveDashboard';
import Card from './Card';
import { fmtMoneyAtLeast as fmtMoney } from '../utils/moneyFormat';

// Signed amounts (movement deltas, Remediating today) and a range's low bound: floored like fmtMoney but without the trailing "at least" "+".
const fmtMoneyBare = (val) => fmtMoney(val, { suffix: false });

// Endpoint marker for one forecast line. Draws the dot and, after commit, reports its Recharts-
// computed pixel position to the chart (see endDotPositions in ExposureTrajectoryChart) so the
// collision-aware label layer can place every $ callout with knowledge of all the others. Reports
// null on unmount (series toggled off, or endpoint moved to another point) so no stale label lingers.
function EndDot({ seriesKey, cx, cy, color, onPosition }) {
  useLayoutEffect(() => {
    onPosition(seriesKey, { cx, cy });
    return () => onPosition(seriesKey, null);
  }, [seriesKey, cx, cy, onPosition]);
  return <circle cx={cx} cy={cy} r={3.5} fill={color} stroke="#fff" strokeWidth={1} />;
}

// Every exposure amount on this card uses the shared "at-least" formatter ($93K+), the same as the
// Top Attack Paths card beside it. Only the Y-axis ticks keep plain rounding: they are the chart's
// scale ($120K gridline), not an amount.
function fmtAxisMoney(val) {
  if (val == null) return '—';
  const n = Number(val);
  if (isNaN(n)) return '—';
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${Math.round(n).toLocaleString()}`;
}

function TrendChartSkeleton() {
  return (
    <Card>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-4 h-4 rounded-full bg-gray-200 animate-pulse" />
        <div className="h-4 w-40 bg-gray-200 rounded animate-pulse" />
      </div>
      <div className="h-[220px] bg-gray-100 rounded animate-pulse flex flex-col items-center justify-center gap-2">
        <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
        <span className="text-xs text-gray-400">Loading exposure data…</span>
      </div>
    </Card>
  );
}

// Shown when the fetch has never succeeded — distinct from TrendChartEmpty so a transient request
// failure never masquerades as "this org has no risk data".
function TrendChartError() {
  return (
    <Card>
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp className="w-4 h-4 text-gray-300" />
        <span className="text-sm font-semibold text-gray-800">Exposure Trajectory</span>
      </div>
      <div className="h-[220px] flex items-center justify-center text-center px-4">
        <p className="text-xs text-amber-600">
          Exposure data couldn't be loaded. It will retry automatically on the next dashboard
          refresh.
        </p>
      </div>
    </Card>
  );
}

// Shown for a brand-new org with no risks yet — never a blank gap on the dashboard.
function TrendChartEmpty() {
  return (
    <Card>
      <div className="flex items-center gap-2 mb-3">
        <Minus className="w-4 h-4 text-gray-300" />
        <span className="text-sm font-semibold text-gray-800">Exposure Trajectory</span>
      </div>
      <div className="h-[220px] flex items-center justify-center text-center px-4">
        <p className="text-xs text-gray-400">
          Exposure history will appear once risks with a calculated financial impact exist for this
          organization.
        </p>
      </div>
    </Card>
  );
}

// Merge independently-dated series (historical ends at "now"; forecasts start at "now") into one
// array recharts can plot as lines sharing an x-axis. Nulls where a series has no point at that
// date so each line only draws over its own range. Committed/sensitivity band are always merged in
// (cheap) even when not rendered — toggling visibility doesn't require re-merging.
// sensLow/sensBand feed a stacked-Area "band" trick: sensLow is an invisible base area up to the
// band's floor, sensBand (High - Low) is the visible fill stacked on top of it — recharts has no
// native low/high band primitive, this is the standard workaround.
function mergeSeries(historical, noActionForecast, reconciledForecast, committedForecast, sensitivityBand, customPaceForecast) {
  const byDate = new Map();
  const upsert = (date, key, amount) => {
    const existing = byDate.get(date) || { date };
    existing[key] = amount;
    byDate.set(date, existing);
  };
  (historical || []).forEach(p => upsert(p.date, 'historical', p.amount));
  (noActionForecast || []).forEach(p => upsert(p.date, 'noAction', p.amount));
  (reconciledForecast || []).forEach(p => upsert(p.date, 'reconciled', p.amount));
  (committedForecast || []).forEach(p => upsert(p.date, 'committed', p.amount));
  (sensitivityBand || []).forEach(p => {
    upsert(p.date, 'sensLow', p.low);
    upsert(p.date, 'sensBand', p.high - p.low);
  });
  (customPaceForecast || []).forEach(p => upsert(p.date, 'customPace', p.amount));
  return Array.from(byDate.values());
}

// Risks/month options for the Custom Pace dropdown — round numbers, 10 as the default rate.
const RISKS_PER_MONTH_OPTIONS = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

// Per the Forecast Exposure naming pass: "P50" is deliberately NOT in the Forecast Exposure label
// yet — that would claim a calibrated percentile this chart doesn't produce (no real Monte Carlo
// distribution behind it yet, just a point estimate). Add "P50" only once real simulation output
// backs it.
const SERIES_LABEL = {
  historical: 'Historical Exposure',
  noAction: 'No Action',
  reconciled: 'Forecast Exposure',
  committed: 'Committed Plan',
};

// The "why" tooltip: below the usual line values, shows the live category breakdown driving the red
// line's growth at this exact day (reconstructed client-side from noActionBreakdown using the same
// formula ProjectExposureForward uses server-side — cheap, and avoids shipping a breakdown for every
// one of the 181 forecast days), and any specific risk(s) whose remediation lands on this exact date
// (from reconciledSteps) — the literal cause of a step down on the blue line.
function ExposureTooltip({ active, payload, label, noActionBreakdown, reconciledSteps, dayIndexByDate, sensitivityByDate, customPaceLabel }) {
  if (!active || !payload?.length) return null;

  // sensLow/sensBand are the internal stacked-Area band keys (see mergeSeries) — technical plumbing,
  // not a value a person reads directly, so they're excluded from the generic rows list and replaced
  // with one dedicated "Sensitivity Range: $low - $high" row below when a band exists for this day.
  const rows = payload.filter(p => p.value != null && p.dataKey !== 'sensLow' && p.dataKey !== 'sensBand');
  const dayIndex = dayIndexByDate.get(label);
  const band = sensitivityByDate?.get(label);
  const bandIsMeaningful = band && Math.abs(band.high - band.low) > 0.5;

  // A hover tooltip tracks the cursor, so it can't be scrolled (moving the mouse to reach a
  // scrollbar just relocates or dismisses it) — laid out as 2 columns instead of folding categories
  // into "Other", so every category stays individually visible while the list's total height is
  // roughly halved (never needs to scroll, and stays clear of the legend/caption below the chart).
  const categories = (dayIndex != null && noActionBreakdown?.length)
    ? noActionBreakdown
        .map(c => ({ ...c, projected: c.currentAmount * (1 + c.annualProbability * (dayIndex / 365)) }))
        .sort((a, b) => b.projected - a.projected)
    : [];
  const categoriesTotal = categories.reduce((sum, c) => sum + c.projected, 0);

  const stepsToday = (reconciledSteps || []).filter(s => s.date === label);
  const shownSteps = stepsToday.slice(0, 4);

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs max-w-[440px]">
      <div className="font-semibold text-gray-800 mb-1">{label}</div>
      {rows.map(r => (
        <div key={r.dataKey} className="flex items-center justify-between gap-3" style={{ color: r.color }}>
          <span>{SERIES_LABEL[r.dataKey] || (r.dataKey === 'customPace' ? customPaceLabel : r.dataKey)}</span>
          <span className="font-semibold">{fmtMoney(r.value)}</span>
        </div>
      ))}

      {bandIsMeaningful && (
        <div className="flex items-center justify-between gap-3 text-purple-500">
          <span>Sensitivity Range</span>
          <span className="font-semibold">{fmtMoneyBare(band.low)} – {fmtMoney(band.high)}</span>
        </div>
      )}

      {categories.length > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-100">
          <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Why it rises</div>
          <div className="grid grid-cols-2 gap-x-3">
            {categories.map(c => (
              <div
                key={c.riskType}
                className="flex items-center justify-between gap-1.5 text-gray-600"
                title={`${c.categoryLabel} — ${(c.annualProbability * 100).toFixed(0)}%/yr`}
              >
                <span className="truncate">{c.categoryLabel}</span>
                <span className="whitespace-nowrap">
                  <span className="text-[9px] text-gray-400">{(c.annualProbability * 100).toFixed(0)}%/yr</span>{' '}
                  <span className="font-medium text-gray-800">{fmtMoney(c.projected)}</span>
                </span>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between gap-2 mt-1 pt-1 border-t border-gray-100 text-gray-700 font-semibold">
            <span>Total</span>
            <span className="whitespace-nowrap">{fmtMoney(categoriesTotal)}</span>
          </div>
        </div>
      )}

      {shownSteps.length > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-100">
          <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Remediating today</div>
          {shownSteps.map(s => (
            <div key={s.riskId} className="flex items-center justify-between gap-2 text-gray-600">
              <span className="truncate">{s.riskTitle}</span>
              <span className="font-medium text-blue-700 whitespace-nowrap">-{fmtMoneyBare(s.amount)}</span>
            </div>
          ))}
          {stepsToday.length > shownSteps.length && (
            <div className="text-gray-400">+{stepsToday.length - shownSteps.length} more</div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ExposureTrajectoryChart() {
  const apiCall = useApi();
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  // Default hidden — Committed Plan is a distinct, real-only view (see backend CommittedForecast
  // doc), not part of the default story. Keeping it opt-in matches the "don't overload the chart"
  // guidance and avoids a near-flat 4th line being on by default for the ~99% of orgs that don't
  // yet set remediation target dates on most risks.
  const [showCommitted, setShowCommitted] = useState(false);
  // Same default-hidden rationale as showCommitted — an uncertainty band is an advanced/opt-in view,
  // not part of the default story most viewers need.
  const [showSensitivity, setShowSensitivity] = useState(false);
  // Custom Pace is always visible (unlike Committed Plan/Sensitivity Range) — the user asked for a
  // 3rd line drawn immediately at the default rate, not an opt-in toggle. Changing the dropdown
  // re-fetches from the backend (the highest-$-first ordering needs the full active risk list,
  // which isn't shipped to the frontend — recomputing this client-side would mean sending the whole
  // risk register down just for this one line).
  const [risksPerMonth, setRisksPerMonth] = useState(10);
  // Distinct from `loaded`: `loaded` is a one-way "has real data ever arrived" gate (drives the big
  // skeleton on first paint only); `refreshing` flips on for EVERY fetch after that — the initial
  // load, a risksPerMonth dropdown change, or a background live-poll tick — so the chart never
  // vanishes back to a full skeleton for an in-place refresh (jarring, and blows away the header
  // controls mid-interaction), but the user still sees a small spinner confirming something's
  // updating rather than silently wondering if their dropdown click did anything.
  const [refreshing, setRefreshing] = useState(false);
  // A failed fetch must never erase data that already loaded — a background live-poll blip would
  // otherwise flip the whole card to the "no risk data" empty state, which reads as the org's data
  // being gone. On failure the last successful payload is kept and a non-destructive warning shown.
  const [fetchFailed, setFetchFailed] = useState(false);

  const fetchTrajectory = useCallback(() => {
    setRefreshing(true);
    apiCall('GET', `/allRisks/exposure-trajectory?historyMonths=6&forecastDays=180&risksPerMonth=${risksPerMonth}`)
      .then(d => { setData(d ?? null); setFetchFailed(false); setLoaded(true); })
      .catch(() => { setFetchFailed(true); setLoaded(true); })
      .finally(() => setRefreshing(false));
  }, [apiCall, risksPerMonth]);

  useEffect(() => { fetchTrajectory(); }, [fetchTrajectory]);

  useLiveDashboard({ refresh: fetchTrajectory });

  // Endpoint pixel positions for the end-of-line $ labels — declared HERE, above the early returns
  // below, because hooks must run in the same order on every render (React #310 otherwise: the
  // skeleton render has N hooks, the loaded render N+3). See the endDotPositions comment further
  // down for why these are state rather than a render-time object.
  const endDotPositionsRef = useRef({});
  const [endDotPositions, setEndDotPositions] = useState({});
  // Stable across renders (useCallback, no deps) so EndDot's effect deps don't churn — EndDot itself
  // lives at module scope for the same reason: an inline component type changes identity every
  // parent render, which would remount every dot and re-fire these writes on each pass.
  const reportEndDot = useCallback((seriesKey, pos) => {
    const current = endDotPositionsRef.current;
    const prev = current[seriesKey];
    if (pos == null) {
      if (!prev) return;
      const { [seriesKey]: _removed, ...rest } = current;
      endDotPositionsRef.current = rest;
      setEndDotPositions(rest);
      return;
    }
    if (prev && prev.cx === pos.cx && prev.cy === pos.cy) return;
    endDotPositionsRef.current = { ...current, [seriesKey]: pos };
    setEndDotPositions(endDotPositionsRef.current);
  }, []);

  if (!loaded) return <TrendChartSkeleton />;
  if (!data && fetchFailed) return <TrendChartError />;
  if (!data || (!data.historical?.length && !data.noActionForecast?.length)) return <TrendChartEmpty />;

  const chartData = mergeSeries(
    data.historical, data.noActionForecast, data.reconciledForecast, data.committedForecast, data.sensitivityBand,
    data.customPaceForecast);
  const customPaceLabel = `Custom Pace (${risksPerMonth}/month)`;
  const nowLabel = data.historical?.length ? data.historical[data.historical.length - 1].date : null;
  const noActionEnd = data.noActionForecast?.length ? data.noActionForecast[data.noActionForecast.length - 1].amount : null;
  const reconciledEnd = data.reconciledForecast?.length ? data.reconciledForecast[data.reconciledForecast.length - 1].amount : null;
  const noActionEndLabel = data.noActionForecast?.length ? data.noActionForecast[data.noActionForecast.length - 1].date : null;
  const reconciledEndLabel = data.reconciledForecast?.length ? data.reconciledForecast[data.reconciledForecast.length - 1].date : null;
  const committedEnd = data.committedForecast?.length ? data.committedForecast[data.committedForecast.length - 1].amount : null;
  const committedEndLabel = data.committedForecast?.length ? data.committedForecast[data.committedForecast.length - 1].date : null;
  // A flat Committed Plan (never moves from "now") means no risk has a target date set, not that
  // zero remediation is planned — those read very differently, so this gets its own empty-state
  // message rather than just showing a flat purple line with no context.
  const committedIsEmpty = committedEnd != null && data.currentExposure != null
    && Math.abs(committedEnd - data.currentExposure) < 0.5;
  const customPaceEnd = data.customPaceForecast?.length ? data.customPaceForecast[data.customPaceForecast.length - 1].amount : null;
  const customPaceEndLabel = data.customPaceForecast?.length ? data.customPaceForecast[data.customPaceForecast.length - 1].date : null;

  // date string -> day offset from "now" (0..horizonDays) — noActionForecast is built server-side
  // one point per day in order, so its array index IS the day offset; reused to reconstruct the
  // per-category breakdown for whichever day the user is hovering.
  const dayIndexByDate = new Map();
  (data.noActionForecast || []).forEach((p, i) => dayIndexByDate.set(p.date, i));

  // date string -> {low, high} for the tooltip's dedicated Sensitivity Range row.
  const sensitivityByDate = new Map();
  (data.sensitivityBand || []).forEach(p => sensitivityByDate.set(p.date, p));
  // The backend's own floor (see BuildReconciledSensitivityBand) only guarantees low != high in the
  // literal floating-point sense — for an org whose undated-risk pool is a small slice of total
  // exposure, the ±15% pace perturbation can produce a real but tiny spread (e.g. $150K on $488M,
  // ~0.03%), which renders as well under a pixel on a chart spanning hundreds of millions on the
  // y-axis: technically present, invisible in practice (QA: TC-ET-12/13 — the toggle should only
  // appear when there's something a person can actually see once they enable it). Requiring the
  // largest daily spread to be at least ~1% of current exposure is a rough proxy for "wide enough to
  // read as a band, not a hairline" at this chart's typical y-axis scale.
  const maxSensitivitySpread = (data.sensitivityBand || []).reduce(
    (max, p) => Math.max(max, Math.abs(p.high - p.low)), 0);
  const sensitivityHasSpread = data.currentExposure > 0
    && (maxSensitivitySpread / data.currentExposure) > 0.01;

  // End-of-line dollar callouts (No Action / Forecast Exposure / Custom Pace / Committed Plan).
  //
  // Placement history, so the next edit doesn't re-walk it: (1) same-x <ReferenceDot>s resolved to
  // the wrong sibling; (2) a per-line slope-based offset (label below the dot on a downward line)
  // landed labels beside the NEXT line's dot; (3) a fixed above-the-dot offset fixed the pairing but
  // two labels still overlapped EACH OTHER whenever two endpoints sat closer than a label's height
  // (~13px — confirmed live: $707.1M rendered half-under $657.1M). The root flaw all three shared:
  // each label was positioned in isolation, blind to the others. No per-label offset can prevent
  // label-vs-label collision, so the labels are now drawn in ONE layer that sees every endpoint:
  // each Line's `dot` callback draws only its endpoint circle and reports its Recharts-computed
  // cx/cy up into endDotPositions (React state); the <Customized> layer reads those,
  // starts every label just above-right of its own dot, sorts by y, and pushes any colliding labels
  // apart to a minimum gap — so each label stays as close to its own dot as the geometry allows and
  // can never sit on top of a neighbor.
  const endLabelByKey = {};
  [
    { key: 'noAction', color: '#dc2626', value: noActionEnd, dateLabel: noActionEndLabel },
    { key: 'reconciled', color: '#2563eb', value: reconciledEnd, dateLabel: reconciledEndLabel },
    { key: 'customPace', color: '#0d9488', value: customPaceEnd, dateLabel: customPaceEndLabel },
    ...(showCommitted && !committedIsEmpty
      ? [{ key: 'committed', color: '#7c3aed', value: committedEnd, dateLabel: committedEndLabel }]
      : []),
  ]
    .filter(s => s.value != null && s.dateLabel)
    .forEach(s => { endLabelByKey[s.key] = s; });

  // Endpoint pixel positions, reported by each EndDot after it commits and consumed by
  // EndLabelsLayer. This used to be a plain object mutated during render and read by <Customized>
  // in the same pass — which only works if Recharts renders Line before Customized in ONE parent
  // pass. In Recharts 3 that isn't guaranteed: Line reads its points from the chart's Redux store
  // and re-renders on its own when layout lands (first paint, container resize), while Customized
  // has no store subscription and only re-renders with this component. So the labels could miss the
  // first paint entirely and stayed at stale coordinates after a resize. Going through state fixes
  // both: EndDot writes only when its cx/cy actually changed (equality guard — positions converge,
  // so this is one extra commit, not a render loop) and that write is what re-renders the layer.
  function renderEndDot(key) {
    return (dotProps) => {
      const spec = endLabelByKey[key];
      if (!spec || dotProps.value == null || dotProps.payload?.date !== spec.dateLabel) return null;
      return <EndDot key={`end-${key}`} seriesKey={key} cx={dotProps.cx} cy={dotProps.cy} color={spec.color} onPosition={reportEndDot} />;
    };
  }

  // One pass over ALL end labels: ideal position is just above-right of the label's own dot; where
  // two ideals collide (endpoints closer than MIN_LABEL_GAP), the lower label is pushed down only
  // as far as needed. Ordering by dot y means a pushed label always moves AWAY from the dots above
  // it and toward its own side, so dot↔label pairing survives the shift.
  function EndLabelsLayer() {
    const MIN_LABEL_GAP = 13;
    const entries = Object.entries(endDotPositions)
      .filter(([key]) => endLabelByKey[key])
      .map(([key, pos]) => ({ ...endLabelByKey[key], ...pos, labelY: pos.cy - 5 }))
      .sort((a, b) => a.cy - b.cy);
    for (let i = 1; i < entries.length; i++) {
      if (entries[i].labelY < entries[i - 1].labelY + MIN_LABEL_GAP)
        entries[i].labelY = entries[i - 1].labelY + MIN_LABEL_GAP;
    }
    return (
      <g>
        {entries.map(e => (
          <text key={`end-label-${e.key}`} x={e.cx + 7} y={e.labelY} textAnchor="start"
            fontSize={11} fontWeight={700} fill={e.color}
            paintOrder="stroke" stroke="#fff" strokeWidth={4} strokeLinejoin="round">
            {fmtMoney(e.value)}
          </text>
        ))}
      </g>
    );
  }

  return (
    <Card>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-amber-500" />
          <span className="text-sm font-semibold text-gray-800">Exposure Trajectory</span>
          {refreshing && <Loader2 className="w-3.5 h-3.5 text-gray-400 animate-spin" aria-label="Refreshing" />}
          <span className="text-xs text-gray-400">(6mo history, 180-day forecast)</span>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-4 text-xs">
            <span className="text-gray-500">Current Exposure: <span className="font-semibold text-gray-800">{fmtMoney(data.currentExposure)}</span></span>
            <span className="text-red-600">No Action: <span className="font-semibold">{fmtMoney(noActionEnd)}</span></span>
            <span className="text-blue-600">Forecast Exposure: <span className="font-semibold">{fmtMoney(reconciledEnd)}</span></span>
            <span className="text-teal-600">{customPaceLabel}: <span className="font-semibold">{fmtMoney(customPaceEnd)}</span></span>
            {showCommitted && (
              <span className="text-purple-600">Committed Plan: <span className="font-semibold">{fmtMoney(committedEnd)}</span></span>
            )}
          </div>
          <label className="flex items-center gap-1.5 text-[11px] text-gray-500">
            Risks/month:
            <select
              value={risksPerMonth}
              onChange={e => setRisksPerMonth(Number(e.target.value))}
              disabled={refreshing}
              aria-busy={refreshing}
              className={`text-[11px] font-medium text-teal-700 border border-teal-200 bg-teal-50 rounded px-1.5 py-0.5 ${
                refreshing ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
              }`}
            >
              {/* "/month" spelled out (not "/mo") per option — the "Risks/month:" label sits outside
                  the native dropdown, so the open option list read on its own (just "10", "20", …)
                  didn't carry the unit, and an abbreviation there read as unclear/cut off. */}
              {RISKS_PER_MONTH_OPTIONS.map(n => (
                <option key={n} value={n}>{n}/month</option>
              ))}
            </select>
            {/* Changing this dropdown re-fetches the whole trajectory from the backend (see
                fetchTrajectory) — disabling the select for that window, plus this spinner, stops a
                user from re-clicking mid-fetch and assuming the first click "didn't work". */}
            {refreshing && <Loader2 className="w-3 h-3 text-teal-600 animate-spin" aria-label="Updating" />}
          </label>
          <button
            type="button"
            onClick={() => setShowCommitted(v => !v)}
            className={`text-[11px] font-medium px-2 py-0.5 rounded-full border transition-colors ${
              showCommitted ? 'border-purple-600 bg-purple-50 text-purple-700' : 'border-gray-200 text-gray-500 hover:border-gray-400'
            }`}
          >
            {showCommitted ? 'Hide' : 'Show'} Committed Plan
          </button>
          {sensitivityHasSpread && (
            <button
              type="button"
              onClick={() => setShowSensitivity(v => !v)}
              className={`text-[11px] font-medium px-2 py-0.5 rounded-full border transition-colors ${
                showSensitivity ? 'border-sky-600 bg-sky-50 text-sky-700' : 'border-gray-200 text-gray-500 hover:border-gray-400'
              }`}
            >
              {showSensitivity ? 'Hide' : 'Show'} Sensitivity Range
            </button>
          )}
          {/* On-demand recompute — same top-right filled Regenerate as the AI Intelligence
              Narrative and Top Attack Paths cards. The trajectory is always computed live from
              current risk data, so this simply re-runs the fetch immediately instead of waiting
              for the next live-poll tick. */}
          <button
            type="button"
            onClick={fetchTrajectory}
            disabled={refreshing}
            title="Recompute the exposure trajectory from the organization's current risk data"
            className="flex items-center gap-1.5 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-60 px-3 py-1.5 rounded-md transition-colors flex-shrink-0"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Regenerating…' : 'Regenerate'}
          </button>
        </div>
      </div>

      {fetchFailed && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded px-2 py-1.5 mb-3">
          Latest refresh failed — showing the last successfully loaded data.
        </p>
      )}

      {/* Phase 3: "why did Current Exposure move" — reuses the same live event reconstruction as the
          Historical line (no snapshot storage), so it only ever explains real movement, never
          forecast drift. Hidden entirely when nothing happened in the window rather than showing a
          "no change" banner that adds noise for no informational value. */}
      {data.movement && (data.movement.newRiskCount > 0 || data.movement.remediatedCount > 0) && (
        <div className="flex items-center gap-2 text-xs text-gray-600 bg-gray-50 border border-gray-100 rounded px-2 py-1.5 mb-3">
          {data.movement.netChange > 0 ? (
            <TrendingUp className="w-3.5 h-3.5 text-red-500 shrink-0" />
          ) : data.movement.netChange < 0 ? (
            <TrendingDown className="w-3.5 h-3.5 text-green-600 shrink-0" />
          ) : (
            <Minus className="w-3.5 h-3.5 text-gray-400 shrink-0" />
          )}
          <span>
            <span className={
              data.movement.netChange > 0 ? 'text-red-600 font-semibold'
                : data.movement.netChange < 0 ? 'text-green-600 font-semibold'
                : 'font-semibold text-gray-700'
            }>
              {data.movement.netChange > 0 ? '+' : data.movement.netChange < 0 ? '−' : ''}{fmtMoneyBare(Math.abs(data.movement.netChange))}
            </span>{' '}
            over the last 30 days
            {data.movement.newRiskCount > 0 && (
              <>
                {' — '}
                <span className="text-red-500 font-medium">+{fmtMoneyBare(data.movement.newRiskExposure)}</span>
                {' '}from {data.movement.newRiskCount} new risk{data.movement.newRiskCount === 1 ? '' : 's'}
              </>
            )}
            {data.movement.remediatedCount > 0 && (
              <>
                {data.movement.newRiskCount > 0 ? ', ' : ' — '}
                <span className="text-green-600 font-medium">-{fmtMoneyBare(data.movement.remediatedExposure)}</span>
                {' '}from {data.movement.remediatedCount} remediation{data.movement.remediatedCount === 1 ? '' : 's'}
              </>
            )}
          </span>
        </div>
      )}

      <ResponsiveContainer width="100%" height={220}>
        {/* right: 68 (was 44) — every end-of-line $ callout (see renderEndDot) sits to the right of
            the last data point; 44px wasn't quite enough room for a "$XXX.XM" string there without
            clipping against the card edge. top: 28 (was 4) — the current-exposure ReferenceDot label
            (y-12) and any upward-trending end-of-line label (yOffset -8, see renderEndDot) draw
            *above* their point; whenever that point already sits near the y-axis max (the normal
            case — current/no-action exposure is usually the highest value on the chart), 4px left no
            room for the label's own height plus its white halo stroke, so it was clipped against the
            SVG's top edge instead of just looking tight against the axis tick. */}
        <ComposedChart data={chartData} margin={{ top: 28, right: 68, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
          <XAxis dataKey="date" tick={{ fontSize: 10 }} interval="preserveStartEnd" minTickGap={24} />
          <YAxis tick={{ fontSize: 10 }} tickFormatter={fmtAxisMoney} width={55} />
          <Tooltip
            wrapperStyle={{ zIndex: 50 }}
            content={(props) => (
              <ExposureTooltip
                {...props}
                noActionBreakdown={data.noActionBreakdown}
                reconciledSteps={data.reconciledSteps}
                dayIndexByDate={dayIndexByDate}
                sensitivityByDate={showSensitivity ? sensitivityByDate : null}
                customPaceLabel={customPaceLabel}
              />
            )}
          />
          <Legend
            iconType="line"
            iconSize={10}
            formatter={v => (v === 'customPace' ? customPaceLabel : (SERIES_LABEL[v] || v))}
            wrapperStyle={{ fontSize: 11 }}
          />
          {nowLabel && (
            <ReferenceLine
              x={nowLabel}
              stroke="#94a3b8"
              strokeDasharray="4 2"
              // Anchored to the bottom of the chart (not 'insideTopRight') so it can never collide
              // with the current-exposure ReferenceDot's "$X.XM" callout above — that label sits
              // directly above the dot at the dot's own y-position, which lands near the top of the
              // chart whenever current exposure is high relative to the y-axis max (as it usually
              // is), overlapping a top-anchored "Now" label. The dot's label also has `isFront`
              // (this line's doesn't), so on a collision "Now" was rendering hidden behind it.
              label={{ value: 'Now', position: 'insideBottomRight', fontSize: 9, fill: '#64748b' }}
            />
          )}
          {/* Animation intentionally off on all 3 lines: a staggered "history draws first, then the
              forecasts split off" reveal was tried, but Recharts animates a dashed Line by redrawing
              its path over time, and a screenshot taken inside that ~1-1.6s window (a very normal
              thing to do right after a page load) catches the red/blue lines mid-reveal, not yet
              reaching the shared "Now" point — reading as a broken/disconnected chart. Rendering the
              final, correct, fully-connected state immediately is more important than the reveal
              flourish for a board-facing number. */}
          {/* Sensitivity Range band: a stacked-Area trick (see mergeSeries) — sensLow is an invisible
              base filling up to the band's floor, sensBand (High - Low) is the visible fill stacked
              on top, together rendering as a Low-to-High band around Forecast Exposure. Drawn before
              the Line elements so the solid/dashed lines paint on top of the fill, not under it. */}
          {showSensitivity && (
            <>
              <Area type="natural" dataKey="sensLow" stackId="sensitivity" stroke="none" fill="#2563eb" fillOpacity={0} isAnimationActive={false} legendType="none" connectNulls={false} />
              {/* fillOpacity bumped from 0.12 -> 0.22, plus a faint top-edge stroke: at 0.12 the band
                  was effectively invisible against the white card background (QA: TC-ET-13) even
                  though the underlying low/high data was correct — this is a legibility fix, not a
                  data fix. */}
              <Area type="natural" dataKey="sensBand" stackId="sensitivity" stroke="#2563eb" strokeOpacity={0.4} strokeWidth={1} fill="#2563eb" fillOpacity={0.22} isAnimationActive={false} name="Sensitivity Range" connectNulls={false} />
            </>
          )}
          {/* All lines use "natural" (cubic spline through every point) instead of "monotone" —
              purely a rendering choice for a smoother board-deck look. The underlying series are
              still discrete daily points (real dated remediations still land on their own specific
              day, e.g. Historical/Forecast Exposure/Committed Plan's steps), so this only rounds the
              corners; it doesn't change any plotted value. Custom Pace itself is no longer a
              once-a-month step — see BuildCustomPaceForecast, which now spreads each month's
              reduction evenly across its 30 days. */}
          <Line type="natural" dataKey="historical" stroke="#1f2937" strokeWidth={1.5} dot={false} connectNulls={false} isAnimationActive={false} />
          <Line type="natural" dataKey="noAction" stroke="#dc2626" strokeWidth={1.5} strokeDasharray="5 3" dot={renderEndDot('noAction')} connectNulls={false} isAnimationActive={false} />
          <Line type="natural" dataKey="reconciled" stroke="#2563eb" strokeWidth={1.5} strokeDasharray="5 3" dot={renderEndDot('reconciled')} connectNulls={false} isAnimationActive={false} />
          {showCommitted && (
            <Line type="natural" dataKey="committed" stroke="#7c3aed" strokeWidth={1.5} strokeDasharray="2 2" dot={renderEndDot('committed')} connectNulls={false} isAnimationActive={false} />
          )}
          {/* Custom Pace: always rendered (not an opt-in toggle like Committed Plan/Sensitivity
              Range) — the user picks a rate from the dropdown and expects to see it drawn
              immediately, starting from the default 10/month. */}
          <Line type="natural" dataKey="customPace" name={customPaceLabel} stroke="#0d9488" strokeWidth={1.5} strokeDasharray="6 2" dot={renderEndDot('customPace')} connectNulls={false} isAnimationActive={false} />

          {/* Amount call-outs: current exposure at the "Now" anchor, and each forecast line's
              180-day figure at its own endpoint — mirrors the board-deck chart style. The "Now" dot is
              deliberately bigger than a plain data-point marker (6px vs the usual ~3.5px) so it visibly
              swallows the first sliver of both the red and blue dashed lines — they read as emerging
              from inside the hub, not as two separate segments merely touching its outer edge. */}
          {nowLabel && data.currentExposure != null && (
            <ReferenceDot
              x={nowLabel} y={data.currentExposure} r={6} fill="#1f2937" stroke="#fff" strokeWidth={2}
              isFront
              label={(props) => {
                const { x, y } = props.viewBox;
                // paintOrder="stroke" + a white stroke behind the fill gives every dollar label its
                // own halo, so it stays legible regardless of what's directly behind it (another
                // label sitting close by, or a dashed line passing near that y) — a proximity/z-order
                // -proof fix instead of one more guess at exact pixel offsets.
                return (
                  <text x={x} y={y - 12} textAnchor="middle" fontSize={11} fontWeight={700} fill="#1f2937"
                    paintOrder="stroke" stroke="#fff" strokeWidth={4} strokeLinejoin="round">
                    {fmtMoney(data.currentExposure)}
                  </text>
                );
              }}
            />
          )}
          {/* End-of-line $ callouts: dots are drawn by each Line's own `dot` callback (an EndDot that
              reports its cx/cy into state after commit), labels by this single collision-aware layer
              — rendered LAST so the labels paint on top of all lines. See the comment by
              endLabelByKey for the full placement history. */}
          <Customized component={EndLabelsLayer} />
        </ComposedChart>
      </ResponsiveContainer>

      {showCommitted && committedIsEmpty && (
        <p className="text-xs text-purple-600 bg-purple-50 border border-purple-100 rounded px-2 py-1.5 mt-2">
          Committed Plan is flat because no open risk currently has a remediation target date set —
          this is different from zero remediation being planned. Set target dates on remediations to
          see this line move.
        </p>
      )}

      {/* "reconstructed" disclosure: no completion timestamp is persisted on remediations yet, so
          the historical line and the 30-day movement date each completed remediation by when its
          remediation record was created — which can differ materially from when it was actually
          completed. Stated explicitly here rather than implying exact history; replace this wording
          once a real CompletedAt exists. */}
      <p className="text-xs text-gray-400 mt-1">
        Solid line is reconstructed history: completed remediations are dated by when their
        remediation record was created (a completion timestamp isn't stored yet), so past exposure
        and the 30-day movement are approximate. Dashed lines project forward from today: red (No Action) assumes
        no further remediation; blue (Forecast Exposure) reflects currently planned remediation
        target dates
        {data.undatedActiveExposure > 0 && (
          data.dailyRemediationVelocity > 0
            ? <> plus a modeled pace for the {fmtMoney(data.undatedActiveExposure)} of open risk with no
                target date set, based on this org's own trailing 90-day remediation rate
                ({fmtMoney(data.dailyRemediationVelocity)}/day)</>
            : <>. {fmtMoney(data.undatedActiveExposure)} of open risk has no target date set and no
                recent completions to model a pace from, so it isn't reflected here yet</>
        )}. Teal ({customPaceLabel}) is a separate what-if scenario, ignoring real target dates
        entirely — it simulates remediating your chosen rate every month, highest-dollar risk first.
        {' '}{showCommitted && (
          <>Purple (Committed Plan) shows only risks with a real, dated remediation target — no
          modeled pace included. </>
        )}{showSensitivity && (
          <>The shaded band is a sensitivity range from perturbing the modeled remediation pace ±15% —
          not a calibrated P10/P90 forecast, just an honest indicator of how much the modeled portion
          could shift.</>
        )}
      </p>
    </Card>
  );
}
